/**
 * t検定ファミリー（Welch / Student / Mann–Whitney / Wilcoxon / 対応あり）の表示用 i18n key。
 * 結果見出し・解析ピッカー・PDF 表紙で共有し、表記の食い違いを防ぐ。
 */

export type TTestDisplayOptions = {
  testType?: string;
  ttestInferential?: string;
  equalVariance?: boolean;
};

function normalizeTestType(testType: string | undefined): 'unpaired' | 'paired' {
  return testType === 'paired' ? 'paired' : 'unpaired';
}

/** 群間比較タブの見出し */
export function tTestComparisonTitleKey(opts: TTestDisplayOptions): string {
  const inferential = opts.ttestInferential ?? 'parametric';
  if (inferential === 'mann-whitney') {
    return 'keisuke.dashboard.groupComparisonTitleMannWhitney';
  }
  if (inferential === 'wilcoxon') {
    return 'keisuke.dashboard.groupComparisonTitleWilcoxon';
  }
  if (normalizeTestType(opts.testType) === 'paired') {
    return 'keisuke.dashboard.groupComparisonTitlePairedTTest';
  }
  return opts.equalVariance
    ? 'keisuke.dashboard.groupComparisonTitleStudent'
    : 'keisuke.dashboard.groupComparisonTitleWelch';
}

/**
 * 短い手法名。
 * - `picker`: 左上トグル。対応なしパラメトリックはカテゴリ名（tabs.ttest）。
 *   推論ピルの「パラメトリック」キーとは共有しない（表記衝突を避ける）。
 * - `detail`: PDF 表紙など、Welch/Student まで明示したいとき
 */
export function tTestMethodShortLabelKey(
  opts: TTestDisplayOptions,
  mode: 'picker' | 'detail' = 'detail'
): string {
  const inferential = opts.ttestInferential ?? 'parametric';
  if (inferential === 'mann-whitney') {
    return 'keisuke.dashboard.ttest.mannWhitney';
  }
  if (inferential === 'wilcoxon') {
    return 'keisuke.dashboard.ttest.wilcoxon';
  }
  if (normalizeTestType(opts.testType) === 'paired') {
    return 'keisuke.dashboard.ttest.pairedParametric';
  }
  if (mode === 'picker') {
    return 'keisuke.tabs.ttest';
  }
  return opts.equalVariance
    ? 'keisuke.dashboard.ttest.student'
    : 'keisuke.dashboard.ttest.welch';
}

const PARAMETRIC_PILL_COLLISION = new Set([
  't検定',
  't 検定',
  't-test',
  't‑test',
  't‑Test',
  'test t',
  'teste t',
  'prueba t',
  't‑тест',
]);

/**
 * 推論ピル用ラベル。locale がカテゴリ名「t検定」に巻き戻ってもピッカーと衝突しない。
 */
export function tTestParametricInferenceLabel(t: (key: string) => string): string {
  const label = t('keisuke.dashboard.ttest.parametricMethod');
  const category = t('keisuke.tabs.ttest');
  if (label && label !== category && !PARAMETRIC_PILL_COLLISION.has(label)) {
    return label;
  }
  return /[\u3040-\u30ff\u4e00-\u9fff]/.test(category) ? 'パラメトリック' : 'Parametric';
}
